import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import { DefaultOverlayPartialAppState, PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { DashboardContent } from "product-test-types";

import { TestAppState } from "../app";
import {
  getFormValuesFromEventPayload,
  isCustomButtonEventPayload,
  PimoFormDialog,
} from "../components/pimo-form";
import { APP_ROUTES } from "../constants";
import {
  fetchDashboard,
  updateManagementSummary,
} from "../helpers/fetch-dashboard";
import { showSnackbar } from "../helpers/show-snackbar";

const STYLE_TEXT = {
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
  "& .MuiInputBase-inputMultiline, & textarea": {
    fontSize: "14px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
  },
  fontSize: "14px",
};

export async function buildManagementSummaryFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"", TestAppState>
) {
  const dialog = app.createView({
    name: "edit-management-summary",
  });

  const editForm = new PimoFormDialog<DashboardContent>();

  const form = dialog.addComponent({ component: editForm });

  registerFormEvents();
  createConfirmationDialog();

  function registerFormEvents() {
    const language = app.getAppState().language;
    form.on("submit", async ({ payload }) => {
      if (isCustomButtonEventPayload(payload) && !payload.hasChanges) {
        closeForm();
        return;
      }

      const submittedDashboardContent =
        getFormValuesFromEventPayload<DashboardContent>(payload);
      if (!submittedDashboardContent) {
        return;
      }

      await updateManagementSummary(
        submittedDashboardContent.managementSummary ?? ""
      );

      const dashboard = await fetchDashboard();
      app.patchAppState({ dashboard });

      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        getLocalizedMessage(language, "allChangesSaved") ||
          "All changes have been saved.",
        "success"
      );

      closeForm();
    });

    form.on("cancel", ({ payload }) => {
      if (isCustomButtonEventPayload(payload) && payload.hasChanges) {
        app.patchAppState({ showEditFormConfirmationDialog: true });

        return;
      }

      closeForm();
    });

    form.on("cancel-with-changes", () => {
      app.patchAppState({ showEditFormConfirmationDialog: true });
    });
  }

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({
      component: PMPopUp,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the settings, these changes will be lost.",
        maxWidth: "sm",

        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",

        cancelButtonFireEvent: () => closeConfirmationDialog(),
        confirmationButtonFireEvent: () => {
          closeConfirmationDialog();
          closeForm();
        },
        onClose: () => closeConfirmationDialog(),
      };
    });
  }

  function closeForm() {
    app.navigate(APP_ROUTES.dashboard);
  }

  function closeConfirmationDialog() {
    app.patchAppState({
      showEditFormConfirmationDialog: false,
    });
  }

  form.mapState((state) => {
    return {
      id: "",
      managementSummary: state.dashboard?.managementSummary ?? "",
      title:
        getLocalizedMessage(
          state.language || "en",
          "updateManagementSummary"
        ) || "Update Management Summary",
      inline: true,
      enableCloseButton: true,
      customButtons: {
        left: [],
        right: [
          {
            id: "cancel",
            label:
              getLocalizedMessage(state.language || "en", "cancel") || "Cancel",
            eventName: "cancel",
            includeFormDataInPayload: true,
          },
          {
            id: "save-and-close",
            label:
              getLocalizedMessage(state.language || "en", "saveAndClose") ||
              "Save & Close",
            eventName: "submit",
            sx: { color: "white" },
            variant: "contained",
            validateBeforeEmit: true,
            includeFormDataInPayload: true,
          },
        ],
      },
    };
  });

  const route = parentRoute.createChildRoute({
    path: APP_ROUTES.editManagementSummary,
    view: dialog,
    isOverlayChild: true,
  });

  route.on("load", async () => {
    await editForm.buildDynamicTabsAsynchronously(async () => {
      const language = app.getAppState().language;

      const tab = editForm.addFormTab({
        label:
          getLocalizedMessage(language, "managementSummary") ||
          "Management Summary",
        id: "management-summary",
      });

      tab.addFormField({
        type: "rich-text",
        label:
          getLocalizedMessage(language, "managementSummary") ||
          "Management Summary",
        multiline: true,
        name: "managementSummary",
        sx: STYLE_TEXT,
      });
    });
  });
}
